/* eslint-disable max-len*/
import { Form, Formik } from 'formik';
import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StyledSingleDropdown from 'src/components/StyledSingleDropdown/StyledSingleDropdown';
import { TooltipAnchor } from 'src/services/app.service';
import { appRoutePaths } from 'src/services/route.service';
import { SealOfBiliteracyListItemModel } from 'src/types/SealOfBiliteracyListItemModel';
import { SealOfBiliteracyListPageValidationSchema } from 'src/types/Validation/SealOfBiliteracyListPageValidationSchema';
import { isDistrictUser, isStateAdmin } from 'src/utilities/userUtilities';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import FormCheckbox from '../../../../components/FormInputs/FormCheckbox/FormCheckbox';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import Modal from '../../../../components/Modal/Modal';
import Panel from '../../../../components/Panel/Panel';
import { useConfirmationModalContext } from '../../../../hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import { selectSealOfBiliteracyCurrentSchoolYear } from 'src/redux/slices/sealOfBiliteracySlice';
import { isSPO } from 'src/utilities/userUtilities';
import { isEditableSchoolYear } from 'src/utilities/sealOfBiliteracyUtilities';
import {
  initialContactFormValues,
  selectSealOfBiliteracyContact,
  selectSealOfBiliteracyDeletingRecord,
  selectSealOfBiliteracyListItems,
  selectSealOfBiliteracyLoadingContact,
  selectSealOfBiliteracyLoadingListItems,
  selectSealOfBiliteracyLoadingSchoolYears,
  selectSealOfBiliteracySchoolYears,
} from '../../../../redux/slices/sealOfBiliteracySlice';
import {
  createSealOfBiliteracyContact,
  fetchSealOfBiliteracyContact,
  fetchSealOfBiliteracyListItems,
  fetchSealOfBiliteracySchoolYears,
  softDeleteSealOfBiliteracyById,
  updateSealOfBiliteracyContact,
} from '../../../../redux/thunks/sealOfBiliteracyThunks';
import { ButtonSize } from '../../../../types/ButtonSize';
import { DialogType } from '../../../../types/DialogTypes';
import { PanelType } from '../../../../types/PanelType';
import { SealOfBiliteracyContactModel } from '../../../../types/SealOfBiliteracyContactModel';
import Student from '../StudentPage';
import './SealOfBiliteracyList.css';

const SealOfBiliteracyList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { schoolYear: urlSchoolYear } = useParams<{ schoolYear: string }>();
  const { showConfirmation, setOptions } = useConfirmationModalContext();
  const contact = useAppSelector(selectSealOfBiliteracyContact);
  const selectedDistrict = useAppSelector(selectSelectedDistrict);
  const isLoadingContactList = useAppSelector(
    selectSealOfBiliteracyLoadingContact
  );
  const currSchoolYear = useAppSelector(
    selectSealOfBiliteracyCurrentSchoolYear
  );
  const isDeletingRecord = useAppSelector(selectSealOfBiliteracyDeletingRecord);
  const [disableTableEdits, setDisableTableEdits] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isReadOnlyUser = (): boolean => {
    if (isStateAdmin() || isDistrictUser()) return false;
    else return true;
  };

  const [resetFormFunction, setResetFormFunction] = useState<
    (() => void) | null
  >(null);
  const listItemsIsLoading = useAppSelector(
    selectSealOfBiliteracyLoadingListItems
  );
  const listItems: SealOfBiliteracyListItemModel[] = useAppSelector(
    selectSealOfBiliteracyListItems
  );
  const schoolYearOptions = useAppSelector(selectSealOfBiliteracySchoolYears);
  const isLoadingSchoolYears = useAppSelector(
    selectSealOfBiliteracyLoadingSchoolYears
  );
  const [selectedSchoolYearOption, setSelectedSchoolYearOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const showAddNewButton =
    isEditableSchoolYear(selectedSchoolYearOption?.value || '', currSchoolYear) &&
    !isReadOnlyUser();

  const showDeleteButton =
    isEditableSchoolYear(selectedSchoolYearOption?.value || '', currSchoolYear) &&
    !isReadOnlyUser();

  useEffect(() => {
    dispatch(fetchSealOfBiliteracyContact({ showLoading: true }));
    dispatch(fetchSealOfBiliteracySchoolYears());
  }, [dispatch]);

  useEffect(() => {
    if (schoolYearOptions.length > 0) {
      if (!urlSchoolYear) {
        const mostRecentYear = schoolYearOptions[0].value;
        navigate(appRoutePaths.SealOfBiliteracyList(mostRecentYear), {
          replace: true,
        });
        return;
      }

      const urlSchoolYearOption = schoolYearOptions.find(
        (option) => option.value === urlSchoolYear
      );
      if (urlSchoolYearOption) {
        const selectedOption = {
          value: urlSchoolYearOption.value,
          label: urlSchoolYearOption.text,
        };
        setSelectedSchoolYearOption(selectedOption);
        dispatch(fetchSealOfBiliteracyListItems(selectedOption.value));
      } else {
        const mostRecentYear = schoolYearOptions[0].value;
        navigate(appRoutePaths.SealOfBiliteracyList(mostRecentYear), {
          replace: true,
        });
      }
    }
  }, [schoolYearOptions, urlSchoolYear, navigate, dispatch]);

  useEffect(() => {
    if (
      !isLoadingContactList &&
      !listItemsIsLoading &&
      !isLoadingSchoolYears &&
      isInitialLoad
    ) {
      setIsInitialLoad(false);
    }
  }, [
    isLoadingContactList,
    listItemsIsLoading,
    isLoadingSchoolYears,
    isInitialLoad,
  ]);

  useEffect(() => {
    setDisableTableEdits(isFormDirty || isSaving || isDeletingRecord);
  }, [isFormDirty, isSaving, isDeletingRecord]);

  const getInitialValues = (): SealOfBiliteracyContactModel => {
    if (contact) {
      return {
        id: contact.id || '',
        leaIdentifier:
          contact.leaIdentifier || selectedDistrict.districtId || '',
        firstName: contact.firstName || '',
        lastOrSurname: contact.lastOrSurname || '',
        email: contact.email || '',
        telephone: contact.telephone || '',
        documentsUploaded: contact.documentsUploaded || false,
      };
    }
    return {
      ...initialContactFormValues,
      leaIdentifier: selectedDistrict.districtId || '',
    };
  };

  const handleSubmit = async (
    values: typeof initialContactFormValues,
    resetForm: () => void
  ): Promise<void> => {
    setIsSaving(true);
    try {
      const contactData = {
        id: values?.id || '',
        leaIdentifier:
          values?.leaIdentifier || selectedDistrict.districtId || '',
        firstName: values?.firstName || '',
        lastOrSurname: values?.lastOrSurname || '',
        email: values?.email || '',
        telephone: values?.telephone || '',
        documentsUploaded: values?.documentsUploaded || false,
      };

      if (contactData.id && contactData.id.trim() !== '') {
        await dispatch(updateSealOfBiliteracyContact(contactData)).unwrap();

        await dispatch(
          fetchSealOfBiliteracyContact({ showLoading: false })
        ).unwrap();

        dispatch(
          createNotification({
            severity: 'success',
            children: 'Seal of biliteracy contact edited',
          })
        );
      } else {
        await dispatch(createSealOfBiliteracyContact(contactData)).unwrap();
        await dispatch(
          fetchSealOfBiliteracyContact({ showLoading: false })
        ).unwrap();

        dispatch(
          createNotification({
            severity: 'success',
            children: 'Seal of biliteracy contact saved',
          })
        );
      }

      setIsFormDirty(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save contact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchoolYear = async (option: any): Promise<void> => {
    if (!option) {
      return;
    }
    setSelectedSchoolYearOption(option);
    dispatch(fetchSealOfBiliteracyListItems(option.value));
    navigate(appRoutePaths.SealOfBiliteracyList(option.value));
  };

  const handleDeleteConfirmation = async (itemId: string): Promise<void> => {
    setOptions({
      title: 'Delete Student?',
      content: 'Are you sure you want to delete this student?',
      confirmText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await dispatch(softDeleteSealOfBiliteracyById(itemId)).unwrap();
          if (selectedSchoolYearOption) {
            await dispatch(
              fetchSealOfBiliteracyListItems(selectedSchoolYearOption.value)
            ).unwrap();
          }
        } catch (error) {
          console.error('Failed to delete record:', error);
        }
        return {
          status: 200,
          data: null,
          error: { type: '', title: '', status: 200, detail: '' },
          aborted: false,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel: () => { },
    });

    const confirmed = await showConfirmation();
    if (!confirmed) {
      return;
    }
  };

  const getToolTip = (dirty: boolean, isValid: boolean): string => {
    if (!dirty && isValid) {
      return '**No changes have been made**';
    } else if (dirty && !isValid) {
      return '**Make sure all required fields are complete**';
    }

    return '**Save Seal of Biliteracy LEA Contact Information Changes**';
  };

  const getCancelToolTip = (dirty: boolean, isValid: boolean): string => {
    if (!dirty && isValid) {
      return 'No changes to cancel';
    }

    return 'Cancel Changes';
  };

  const handleCancelClick = (): void => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = (): void => {
    if (resetFormFunction) {
      resetFormFunction();
    }
    setShowCancelModal(false);
  };

  const handleCancelCancel = (): void => {
    setShowCancelModal(false);
  };

  const hasNoContactData = (): boolean => {
    if (!contact || !contact.documentsUploaded) return true;
    return false;
  };

  return (
    <Student
      pageClass="data-input--student--seal-of-biliteracy--list"
      isLoading={
        isInitialLoad ||
        isLoadingContactList ||
        listItemsIsLoading ||
        isLoadingSchoolYears
      }
      loadingDataId={'seal-of-biliteracy-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Seal of Biliteracy</h4>
            <span style={{ color: 'red' }}>* Required Field</span>
          </div>
          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={SealOfBiliteracyListPageValidationSchema}
              //eslint-disable-next-line @typescript-eslint/no-empty-function
              onSubmit={() => { }}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
            >
              {({ dirty, isValid, resetForm, values }) => {
                if (dirty !== isFormDirty) {
                  setIsFormDirty(dirty);
                }

                if (resetForm !== resetFormFunction) {
                  setResetFormFunction(() => resetForm);
                }

                return (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }}
                  >
                    <Panel
                      panelType={PanelType.INFO}
                      heading="Seal of Biliteracy LEA Contact Information"
                    >
                      <div className="contacts-container">
                        <div>
                          <div className="field-row">
                            <FormTextbox
                              displayName="First Name"
                              field="firstName"
                              disabled={isReadOnlyUser()}
                              validationSchema={
                                SealOfBiliteracyListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Last Name"
                              field="lastOrSurname"
                              disabled={isReadOnlyUser()}
                              validationSchema={
                                SealOfBiliteracyListPageValidationSchema
                              }
                            />
                          </div>
                          <div className="field-row">
                            <FormTextbox
                              displayName="Email"
                              field="email"
                              disabled={isReadOnlyUser()}
                              validationSchema={
                                SealOfBiliteracyListPageValidationSchema
                              }
                            />
                            <FormTextbox
                              displayName="Telephone Number"
                              field="telephone"
                              disabled={isReadOnlyUser()}
                              validationSchema={
                                SealOfBiliteracyListPageValidationSchema
                              }
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <ActionButton
                            classes="button"
                            size={ButtonSize.SMALL}
                            onClick={handleCancelClick}
                            dataTestId="cancel-button"
                            tooltipText={getCancelToolTip(dirty, isValid)}
                            disabled={!dirty || isSaving}
                          >
                            <span>Cancel</span>
                          </ActionButton>
                          <ActionButton
                            classes="button--secondary"
                            size={ButtonSize.SMALL}
                            buttonType="button"
                            dataTestId="submit-button"
                            tooltipText={getToolTip(dirty, isValid)}
                            disabled={
                              !isValid ||
                              !dirty ||
                              !values.documentsUploaded ||
                              isSaving
                            }
                            loading={isSaving}
                            onClick={async (
                              e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                              e.preventDefault();
                              e.stopPropagation();
                              await handleSubmit(values, resetForm);
                              return false;
                            }}
                          >
                            <span>Save</span>
                          </ActionButton>
                        </div>
                      </div>
                    </Panel>
                    <Panel
                      panelType={PanelType.INFO}
                      heading="Upload Documents"
                    >
                      <div className="field-row">
                        <FormCheckbox
                          displayName="The (3) supporting LEA Seal of Biliteracy documents have been uploaded to SharePoint"
                          field="documentsUploaded"
                          disabled={isReadOnlyUser()}
                          validationSchema={
                            SealOfBiliteracyListPageValidationSchema
                          }
                          classConfig={{
                            labelClasses: 'forced-required-label',
                          }}
                        />
                      </div>
                    </Panel>
                  </Form>
                );
              }}
            </Formik>
          </div>
          <br />
          <div>
            <div className="filters-selection-container">
              <div className="dropdown-selection">
                <StyledSingleDropdown
                  fieldName="schoolYear"
                  options={schoolYearOptions}
                  onChange={handleOnChangeOfSchoolYear}
                  value={selectedSchoolYearOption}
                  isSearchable={false}
                  isClearable={false}
                  labelText="Graduation Year"
                  showLabel={true}
                  disabled={disableTableEdits}
                  tooltipText={
                    disableTableEdits
                      ? isSaving
                        ? 'Cannot change school year while saving contact'
                        : 'Cannot change school year while form has unsaved changes'
                      : 'Select school year'
                  }
                />
              </div>
              {showAddNewButton && (
                <div>
                  <ActionButton
                    onClick={() =>
                      navigate(
                        appRoutePaths.SealOfBiliteracyInput(
                          'new',
                          selectedSchoolYearOption?.value || ''
                        )
                      )
                    }
                    classes="no-wrap-text new-button"
                    tooltipText={
                      disableTableEdits
                        ? isSaving
                          ? 'Cannot add new record while saving contact'
                          : 'Cannot add new record while form has unsaved changes'
                        : 'Add New Seal of Biliteracy'
                    }
                    disabled={!isEditableSchoolYear(selectedSchoolYearOption?.value || '', currSchoolYear) || disableTableEdits || hasNoContactData() || isSPO()}
                  >
                    <>Add New</>
                  </ActionButton>
                </div>
              )}
            </div>
            <div className="table-container">
              <table cellSpacing={0} className="page-view-table">
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>MSIS ID</th>
                    <th>School</th>
                    <th>Language</th>
                    <th>Seal of Biliteracy</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listItems?.length > 0 ? (
                    listItems.map((item) => (
                      <tr key={`${item.id}`} data-testid={item.id}>
                        <td>
                          {item.studentLastOrSurname}
                          {', '}
                          {item.studentFirstName}
                        </td>
                        <td>{item.studentIdentifier}</td>
                        <td>
                          {item.schoolName}
                          {item.schoolIdentifier
                            ? ` (${item.schoolIdentifier})`
                            : ''}
                        </td>
                        <td>{item.language || ''}</td>
                        <td>{item.sealAwarded || ''}</td>
                        <td>
                          <div className="action--column">
                            <button
                              data-testid={`edit-button-${item.id}`}
                              aria-label="Edit"
                              onClick={() =>
                                navigate(
                                  appRoutePaths.SealOfBiliteracyInput(
                                    item.id,
                                    selectedSchoolYearOption?.value || ''
                                  )
                                )
                              }
                              data-tooltip-id={TooltipAnchor}
                              data-tooltip-content={
                                disableTableEdits
                                  ? isSaving
                                    ? 'Cannot edit while saving contact'
                                    : 'Cannot edit while form has unsaved changes'
                                  : 'Edit/View Seal of Biliteracy'
                              }
                              disabled={disableTableEdits}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: disableTableEdits
                                  ? 'not-allowed'
                                  : 'pointer',
                                opacity: disableTableEdits ? 0.5 : 1,
                              }}
                            >
                              <img src={'/images/icon-edit.svg'} alt="Edit" />
                            </button>
                            {showDeleteButton && (
                              <ActionButton
                                data-testid={`delete-button-${item.id}`}
                                onClick={() => {
                                  if (!disableTableEdits) {
                                    handleDeleteConfirmation(item.id);
                                  }
                                }}
                                tooltipText={
                                  disableTableEdits
                                    ? isSaving
                                      ? 'Cannot delete while saving contact'
                                      : isDeletingRecord
                                        ? 'Deleting record...'
                                        : 'Cannot delete while form has unsaved changes'
                                    : 'Delete Seal of Biliteracy'
                                }
                                disabled={disableTableEdits}
                                loading={isDeletingRecord}
                                classes="delete-button-icon"
                              >
                                <img
                                  src={'/images/icon-trash.svg'}
                                  alt="Delete"
                                />
                              </ActionButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="no-results-row">
                      <td colSpan={6}>No results match your search criteria</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <Modal
          type={DialogType.CONFIRM}
          open={showCancelModal}
          onClose={handleCancelCancel}
          title={'Are you sure?'}
        >
          <div className="confirm-container" data-testid="confirmation-modal">
            <p data-testid="cancel-message">
              Your changes have not been saved. Do you wish to continue?
            </p>
            <div className="button-row">
              <ActionButton
                onClick={handleCancelConfirm}
                dataTestId="yes-button"
                tooltipText={'Navigate to the last page'}
              >
                <span>Yes</span>
              </ActionButton>
              <ActionButton
                classes="button--secondary"
                onClick={handleCancelCancel}
                dataTestId="no-button"
                tooltipText={'Stay on existing page'}
              >
                <span>No</span>
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </Student>
  );
};

export default SealOfBiliteracyList;
